/**
 * DiffusionNouvellePage — composer une diffusion à retour attendu (mig 254).
 *
 * ─── CE QUI DISTINGUE CET ÉCRAN DE LA MESSAGERIE ────────────────────────────
 *
 * On n'y coche pas des destinataires un par un : on DÉCRIT une cible (tous, des
 * secteurs, des personnes). C'est ce qui permet à un agent recruté la semaine
 * suivante de recevoir quand même la campagne DUERP encore ouverte — le trou
 * que les groupes figés de la messagerie (mig 236) ne pouvaient pas boucher.
 *
 * Et on choisit ce qu'on attend en retour : « j'ai pris connaissance » quand un
 * document change, « c'est fait » quand on demande un geste. Deux libellés, un
 * seul mécanisme — et dans les deux cas la réponse est définitive.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Alert, Button, Input, Field, Badge, ModuleHeader } from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { api, ApiException } from '@/lib/api';
import type { DiffusionCibleType, RetourAttendu } from '@roronoa-collectivites/shared-types';
import { Megaphone, Send, Info, Users, CircleCheck } from 'lucide-react';

/** Les secteurs proposés. Mêmes clés que `UserService`, libellés métier. */
const SECTEURS: { key: string; label: string }[] = [
  { key: 'grc', label: 'Relation citoyen (GRC)' },
  { key: 'service_technique', label: 'Service technique' },
  { key: 'scolaire_cantine', label: 'Scolaire & cantine' },
  { key: 'petite_enfance', label: 'Petite enfance' },
  { key: 'associations', label: 'Associations' },
  { key: 'urbanisme', label: 'Urbanisme' },
  { key: 'comptable', label: 'Comptabilité' },
  { key: 'rh', label: 'Ressources humaines' },
];

export function DiffusionNouvellePage() {
  const navigate = useNavigate();

  const [objet, setObjet] = useState('');
  const [corps, setCorps] = useState('');
  const [retourAttendu, setRetourAttendu] = useState<RetourAttendu>('prise_connaissance');
  const [cibleType, setCibleType] = useState<DiffusionCibleType>('secteurs');
  const [secteurs, setSecteurs] = useState<string[]>([]);
  const [dateLimite, setDateLimite] = useState('');
  const [inclutNouveaux, setInclutNouveaux] = useState(true);
  const [documents, setDocuments] = useState<{ id: string; titre: string }[]>([]);
  const [docsChoisis, setDocsChoisis] = useState<string[]>([]);
  // M20 (retour Manon) : la diffusion par secteur n'atteint pas les admins
  // (sans service affecté) — on peut les ajouter nommément.
  const [admins, setAdmins] = useState<{ id: string; nom: string }[]>([]);
  const [adminsChoisis, setAdminsChoisis] = useState<string[]>([]);
  useEffect(() => {
    api.diffusions.adminsIncluables().then(setAdmins).catch(() => setAdmins([]));
  }, []);

  const [portee, setPortee] = useState<{ peutDiffuserATous: boolean; monSecteur: string | null } | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [dernierId, setDernierId] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const p = await api.diffusions.maPortee();
        setPortee(p);
        // Pré-sélection utile : un manager vise d'abord son propre secteur.
        if (p.monSecteur) setSecteurs([p.monSecteur]);
      } catch { /* la garde serveur reste seule juge */ }
      try {
        // Seuls les documents « tous agents » sont diffusables — le serveur le
        // revérifie, mais autant ne pas proposer ce qui sera refusé.
        const docs = await api.rh.listDocuments({});
        setDocuments(
          docs.filter((d) => d.visibilite === 'agents').map((d) => ({ id: d.id, titre: d.titre }))
        );
      } catch { /* la bibliothèque peut être vide */ }
    })();
  }, []);

  function basculerSecteur(k: string) {
    setSecteurs((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  }
  function basculerDoc(id: string) {
    setDocsChoisis((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const pretAEnvoyer =
    objet.trim().length > 0 &&
    corps.trim().length > 0 &&
    (cibleType !== 'secteurs' || secteurs.length > 0);

  async function envoyer() {
    setEnvoi(true); setError(null); setSucces(null);
    try {
      const r = await api.diffusions.creer({
        objet: objet.trim(),
        corps: corps.trim(),
        retourAttendu,
        cibleType,
        cibleServices: cibleType === 'secteurs' ? secteurs : undefined,
        documentIds: docsChoisis.length ? docsChoisis : undefined,
        dateLimite: dateLimite || null,
        // Sur « les présents », le back force déjà `false` : la cible est un
        // constat daté. On n'envoie pas une valeur que le serveur ignorerait.
        inclutNouveauxArrivants: cibleType === 'presents' ? false : inclutNouveaux,
        // « Tous » inclut déjà les admins : l'ajout n'a de sens qu'ailleurs.
        inclureAdminIds: cibleType !== 'tous' && adminsChoisis.length ? adminsChoisis : undefined,
      });
      setSucces(`Diffusion envoyée à ${r.destinataires} agent${r.destinataires > 1 ? 's' : ''}.`);
      setDernierId(r.id);
      setObjet(''); setCorps(''); setDocsChoisis([]); setDateLimite('');
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Envoi impossible');
    } finally { setEnvoi(false); }
  }

  return (
    <div className="min-h-screen text-ink">
      {/* Le bandeau mairie manquait : sans lui, aucun retour au tableau de bord
          depuis ces écrans. Remonté par la mairie le 28/07. */}
      <AppHeader module="Messagerie interne" accent="grc" />
      <ModuleHeader accent="grc" icon={Megaphone} eyebrow="Messagerie interne" title="Nouvelle diffusion" />

      <main id="main-content" className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        {error && <Alert variant="error" className="mb-4">{error}</Alert>}
        {succes && (
          <Alert variant="success" className="mb-4">
            {succes}
            {dernierId && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => navigate(`/diffusions/${dernierId}/suivi`)}
                  className="font-medium underline underline-offset-2"
                >
                  Voir qui a répondu
                </button>
              </>
            )}
          </Alert>
        )}

        <Alert variant="info" className="mb-4">
          Une diffusion demande une réponse à chaque agent. Elle reste affichée dans
          son espace tant qu'il n'a pas répondu, et sa réponse est définitive.
        </Alert>

        <Card variant="glass" padded className="space-y-4">
          <Field label="Objet" htmlFor="dif-objet" required>
            <Input id="dif-objet" value={objet} onChange={(e) => setObjet(e.target.value)}
              placeholder="Le DUERP a été mis à jour" />
          </Field>

          <Field label="Message" htmlFor="dif-corps" required>
            <textarea
              id="dif-corps" value={corps} onChange={(e) => setCorps(e.target.value)} rows={5}
              placeholder="Merci d'en prendre connaissance. Il est disponible dans vos documents RH."
              className="w-full rounded-r-lg border border-border bg-surface/60 p-3 text-sm text-ink focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg"
            />
          </Field>

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-ink">Ce que vous attendez en retour</legend>
            <div className="flex flex-wrap gap-2">
              {([
                ['prise_connaissance', 'J\'ai pris connaissance', 'Un document a changé, il faut le lire'],
                ['action_faite', 'C\'est fait', 'Vous demandez un geste concret'],
              ] as const).map(([k, label, aide]) => (
                <button
                  key={k} type="button" onClick={() => setRetourAttendu(k)}
                  aria-pressed={retourAttendu === k}
                  className={`flex-1 min-w-[220px] rounded-r-lg border p-3 text-left transition-colors ${
                    retourAttendu === k
                      ? 'border-grc/50 bg-grc/10' : 'border-border bg-surface/50 hover:border-border-strong'
                  }`}
                >
                  <span className="block font-medium text-ink">« {label} »</span>
                  <span className="mt-0.5 block text-xs text-ink-muted">{aide}</span>
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-ink">À qui</legend>
            <div className="flex flex-wrap gap-2">
              <button
                type="button" onClick={() => setCibleType('secteurs')}
                aria-pressed={cibleType === 'secteurs'}
                className={`inline-flex h-9 items-center gap-2 rounded-r-lg border px-3 text-sm ${
                  cibleType === 'secteurs' ? 'border-grc/50 bg-grc/10 font-medium' : 'border-border bg-surface/50'
                }`}
              >
                <Users className="h-4 w-4" aria-hidden="true" /> Par secteur
              </button>
              <button
                type="button" onClick={() => setCibleType('tous')}
                aria-pressed={cibleType === 'tous'}
                disabled={portee ? !portee.peutDiffuserATous : false}
                className={`inline-flex h-9 items-center gap-2 rounded-r-lg border px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                  cibleType === 'tous' ? 'border-grc/50 bg-grc/10 font-medium' : 'border-border bg-surface/50'
                }`}
              >
                <Megaphone className="h-4 w-4" aria-hidden="true" /> Tous les agents
              </button>
              {/* Item 6.2. Même garde de portée que « tous » : cette cible
                  traverse tous les secteurs, elle n'est donc pas ouverte à une
                  brique qui ne diffuse qu'au sien. */}
              <button
                type="button" onClick={() => setCibleType('presents')}
                aria-pressed={cibleType === 'presents'}
                disabled={portee ? !portee.peutDiffuserATous : false}
                className={`inline-flex h-9 items-center gap-2 rounded-r-lg border px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                  cibleType === 'presents' ? 'border-grc/50 bg-grc/10 font-medium' : 'border-border bg-surface/50'
                }`}
              >
                <CircleCheck className="h-4 w-4" aria-hidden="true" /> Les présents aujourd'hui
              </button>
            </div>

            {cibleType === 'presents' && (
              <p className="mt-2 flex items-start gap-2 text-xs text-ink-subtle">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                La liste est arrêtée au moment de l'envoi : un agent en congé
                validé ne la reçoit pas, et personne n'y sera ajouté ensuite.
                Un agent sans planning saisi est considéré présent.
              </p>
            )}

            {portee && !portee.peutDiffuserATous && (
              <p className="mt-2 flex items-start gap-2 text-xs text-ink-subtle">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                Votre brique ne diffuse qu'à son secteur. Le maire peut l'autoriser
                à s'adresser à tous depuis les paramètres de la commune.
              </p>
            )}

            {cibleType === 'secteurs' && (
              <div className="mt-3 flex flex-wrap gap-2">
                {SECTEURS.map((s) => {
                  const choisi = secteurs.includes(s.key);
                  const interdit = portee ? !portee.peutDiffuserATous && s.key !== portee.monSecteur : false;
                  return (
                    <button
                      key={s.key} type="button" disabled={interdit}
                      onClick={() => basculerSecteur(s.key)}
                      aria-pressed={choisi}
                      className={`inline-flex h-8 items-center rounded-r-pill border px-3 text-xs disabled:cursor-not-allowed disabled:opacity-40 ${
                        choisi ? 'border-grc/50 bg-grc/10 font-medium text-ink' : 'border-border bg-surface/50 text-ink-muted'
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}

            {cibleType !== 'tous' && admins.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-ink">Inclure aussi (maire, élus, administrateurs)</p>
                <p className="mb-2 text-xs text-ink-muted">
                  Les administrateurs n'appartiennent à aucun secteur : cochez ceux qui doivent recevoir cette diffusion.
                </p>
                <div className="flex flex-wrap gap-2">
                  {admins.map((a) => {
                    const choisi = adminsChoisis.includes(a.id);
                    return (
                      <button
                        key={a.id} type="button" aria-pressed={choisi}
                        onClick={() => setAdminsChoisis((l) => (choisi ? l.filter((x) => x !== a.id) : [...l, a.id]))}
                        className={`inline-flex h-8 items-center rounded-r-pill border px-3 text-xs ${
                          choisi ? 'border-grc/50 bg-grc/10 font-medium text-ink' : 'border-border bg-surface/50 text-ink-muted'
                        }`}
                      >
                        {a.nom}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </fieldset>

          {documents.length > 0 && (
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-ink">Documents à signaler</legend>
              <div className="flex flex-wrap gap-2">
                {documents.map((d) => (
                  <button
                    key={d.id} type="button" onClick={() => basculerDoc(d.id)}
                    aria-pressed={docsChoisis.includes(d.id)}
                    className={`inline-flex h-8 items-center rounded-r-pill border px-3 text-xs ${
                      docsChoisis.includes(d.id)
                        ? 'border-grc/50 bg-grc/10 font-medium text-ink'
                        : 'border-border bg-surface/50 text-ink-muted'
                    }`}
                  >
                    {d.titre}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-ink-subtle">
                Seuls les documents visibles de tous les agents sont proposés.
              </p>
            </fieldset>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date limite" htmlFor="dif-limite" hint="Facultative — passée, la campagne se ferme">
              <Input id="dif-limite" type="date" value={dateLimite}
                onChange={(e) => setDateLimite(e.target.value)} />
            </Field>
            {/* Sur « les présents », proposer d'inclure les futurs arrivants
                serait une case qui ment : le back l'ignore, et la cible est un
                constat daté. On la retire plutôt que de la laisser inopérante. */}
            <div className={`flex items-end ${cibleType === 'presents' ? 'hidden' : ''}`}>
              <label className="flex items-start gap-2 text-sm text-ink">
                <input
                  type="checkbox" checked={inclutNouveaux}
                  onChange={(e) => setInclutNouveaux(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border"
                />
                <span>
                  Inclure les agents recrutés après l'envoi
                  <span className="mt-0.5 block text-xs text-ink-muted">
                    Recommandé pour le DUERP : un nouvel arrivant doit en prendre
                    connaissance comme les autres.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
            <Button variant="grc" loading={envoi} disabled={!pretAEnvoyer} onClick={() => void envoyer()}>
              <Send className="h-4 w-4" aria-hidden="true" /> Envoyer la diffusion
            </Button>
            <Button variant="glass" onClick={() => navigate('/grc/messagerie')}>Retour à la messagerie</Button>
            {cibleType === 'tous' && <Badge variant="urgent" size="sm">Tous les agents</Badge>}
            {cibleType === 'presents' && <Badge variant="info" size="sm">Présents à l'instant de l'envoi</Badge>}
          </div>
        </Card>
      </main>
    </div>
  );
}
